/** Small DOM helpers shared by the menu modules. `el`, `clamp` and `Timers` are the HUD's own. */

import { el } from '../hud/dom';

export { clamp, el } from '../hud/dom';
export { Timers } from '../hud/timers';

/** A real `<button>`: every action in the menus is one, so Tab, Enter and Space work unaided. */
export function button(label: string, onClick: () => void, extraClass = ''): HTMLButtonElement {
  const node = el('button', extraClass === '' ? 'menu-btn' : `menu-btn ${extraClass}`, label);
  node.type = 'button';
  node.addEventListener('click', onClick);
  return node;
}

/** A span whose tooltip says what its abbreviation means: `RP 30` on screen, the words on hover. */
export function hinted(className: string, text: string, hint: string): HTMLElement {
  const node = el('span', className, text);
  node.title = hint;
  return node;
}

/** The one visibility switch the menus use: toggles `menu-hidden`. */
export function show(node: HTMLElement, visible: boolean): void {
  node.classList.toggle('menu-hidden', !visible);
}

/** An element holding `children`, for the many small rows and columns the screens build. */
export function box(className: string, ...children: Array<HTMLElement | string>): HTMLDivElement {
  const node = el('div', className);
  node.append(...children);
  return node;
}

/** A titled panel; returns the panel and the element content goes into. */
export function panel(title: string, extraClass = ''): { panel: HTMLElement; body: HTMLElement } {
  const node = el('div', extraClass === '' ? 'menu-panel menu-col' : `menu-panel menu-col ${extraClass}`);
  const body = el('div', 'menu-col menu-grow menu-scroll');
  node.append(el('div', 'menu-panel-title', title), body);
  return { panel: node, body };
}

/** A single-line text input with a visible label; the label is also its accessible name. */
export function textField(
  label: string,
  value: string,
  maxLength: number,
): { row: HTMLElement; input: HTMLInputElement } {
  const input = el('input', 'menu-input menu-grow');
  input.type = 'text';
  input.value = value;
  input.maxLength = maxLength;
  input.spellcheck = false;
  input.autocomplete = 'off';
  input.setAttribute('aria-label', label);
  const row = box('menu-field', el('span', 'menu-field-label', label), input);
  return { row, input };
}

/** `+1,250` / `-300` / `0`, the way the debrief prints points. */
export function signed(points: number): string {
  const n = Math.round(points);
  const body = Math.abs(n).toLocaleString('en-US');
  return n > 0 ? `+${body}` : n < 0 ? `-${body}` : '0';
}

/** Show a thrown error's message where the player can read it (08 §5: never hide a number). */
export function errorMessage(err: unknown): string {
  if (err instanceof Error && err.message !== '') return err.message;
  return typeof err === 'string' && err !== '' ? err : 'THE ORDER COULD NOT BE CARRIED OUT';
}
